import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, Users, FileText, AlertTriangle, Vote, Bell, Heart } from 'lucide-react';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useImpersonation } from '../../../contexts/ImpersonationContext';
import { useAuth } from '../../../hooks/useAuth';
import { getCommuneConfig } from '../../../api/stats';
import { ExportContext } from './export';
import { usePeriod, PeriodBar, PrintHeader, TabBar } from './period';
import { OverviewTab } from './OverviewTab';
import { AudienceTab } from './AudienceTab';
import { ContentTab } from './ContentTab';
import { RequestsTab } from './RequestsTab';
import { ParticipationTab } from './ParticipationTab';
import { NotificationsTab } from './NotificationsTab';
import { CommunityTab } from './CommunityTab';
/**
 * Page Statistiques (STATISTIQUES.md, section 6). La période, l'onglet et la
 * comparaison sont gardés dans l'adresse : un lien copié rouvre la même vue.
 */
export default function StatisticsPage() {
    const period = usePeriod();
    const { params, update, from, to, q, compare } = period;
    const { impersonated } = useImpersonation();
    const { user } = useAuth();
    const { data: config } = useQuery({
        queryKey: ['commune-config', impersonated?.slug],
        queryFn: getCommuneConfig,
        staleTime: 5 * 60 * 1000,
    });
    const modules = config?.enabled_modules ?? [];
    const hasRequests = ['signalement', 'messagerie', 'demarches'].some((m) => modules.includes(m));
    const hasParticipation = ['sondages', 'projets_citoyens', 'agenda'].some((m) => modules.includes(m));
    const tabs = [
        { key: 'ensemble', label: "Vue d'ensemble", icon: _jsx(LayoutDashboard, { size: 14 }), visible: true },
        { key: 'audience', label: 'Audience', icon: _jsx(Users, { size: 14 }), visible: true },
        { key: 'contenus', label: 'Contenus', icon: _jsx(FileText, { size: 14 }), visible: true },
        { key: 'demandes', label: 'Signalements et demandes', icon: _jsx(AlertTriangle, { size: 14 }), visible: hasRequests },
        { key: 'participation', label: 'Participation', icon: _jsx(Vote, { size: 14 }), visible: hasParticipation },
        { key: 'notifications', label: 'Notifications', icon: _jsx(Bell, { size: 14 }), visible: true },
        { key: 'communaute', label: 'Communauté', icon: _jsx(Heart, { size: 14 }), visible: true },
    ];
    const visibleTabs = tabs.filter((t) => t.visible);
    const requested = params.get('tab');
    const tab = visibleTabs.some((t) => t.key === requested) ? requested : 'ensemble';
    // Paramètres propres à un onglet, effacés en changeant d'onglet.
    const TAB_PARAMS = ['type', 'content'];
    const goTo = (next, extra = {}) => {
        update({ tab: next === 'ensemble' ? null : next, ...Object.fromEntries(TAB_PARAMS.map((k) => [k, null])), ...extra });
        window.scrollTo({ top: 0 });
    };
    const tabProps = { q, modules, goTo, params, setParam: (k, v) => update({ [k]: v }) };
    const tabLabel = tabs.find((t) => t.key === tab).label;
    const communeName = impersonated?.name ?? user?.commune?.name ?? 'Commune';
    const exportContext = {
        communeName,
        communeSlug: impersonated?.slug ?? user?.commune?.slug ?? 'commune',
        from,
        to,
        tabKey: tab,
        tabLabel,
        compare,
    };
    return (_jsx(ExportContext.Provider, { value: exportContext, children: _jsxs("div", { "data-stats-root": true, children: [_jsx(PrintHeader, { scope: `de ${communeName}`, title: tabLabel, period: period }), _jsx("div", { className: "print:hidden", children: _jsx(PageHeader, { title: "Statistiques", subtitle: "Fr\u00E9quentation de l'application et du site, activit\u00E9 des services. Mesure anonyme, sans cookie ni service tiers." }) }), _jsx(PeriodBar, { period: period }), _jsx(TabBar, { tabs: visibleTabs, active: tab, onSelect: (k) => goTo(k) }), tab === 'ensemble' && _jsx(OverviewTab, { ...tabProps }), tab === 'audience' && _jsx(AudienceTab, { ...tabProps }), tab === 'contenus' && _jsx(ContentTab, { ...tabProps }), tab === 'demandes' && _jsx(RequestsTab, { ...tabProps }), tab === 'participation' && _jsx(ParticipationTab, { ...tabProps }), tab === 'notifications' && _jsx(NotificationsTab, { ...tabProps }), tab === 'communaute' && _jsx(CommunityTab, { ...tabProps })] }) }));
}
